import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { Tooltip } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC } from 'react';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import BestValueBadge from '@/components/Subscription/BestValueBadge';
import {
  borderColorClassName,
  borderT,
} from '@/components/Subscription/PlanTable';
import Pricing from '@/components/Subscription/PlanTable/Columns/HeaderCell/Pricing';
import { plansInterface } from '@/components/Subscription/PlanTable/ResponsiveTable';

interface Props {
  index: number;
  item: plansInterface;
  setData?: React.Dispatch<React.SetStateAction<number[]>>;
  data: number[];
  items: plansInterface[];
}

const Item: FC<Props> = ({ index, item, setData, data, items }) => {
  const titleClass = ' text-sm font-normal leading-[22px]';

  const isOpen = data.includes(item.id);

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: item.id * 0.1 + 0.2 }}
    >
      <motion.button
        onClick={() =>
          setData?.((prev) =>
            prev.includes(item.id)
              ? prev.filter((id) => id !== item.id)
              : [...prev, item.id],
          )
        }
        className={`
          ${borderColorClassName}
          ${borderT}
          ${isOpen ? 'bg-primary/15' : 'bg-black'}
          ${items.length - 1 === index ? '' : 'border-b border-white/20'}
          px-6 py-5 flex items-center justify-between w-full
        `}
      >
        <p
          className={`
            ${isOpen ? 'text-primary' : 'text-white'}
            text-sm leading-[22px] font-semibold
          `}
        >
          {item.title}
        </p>
        <motion.div
          animate={{ rotate: isOpen ? -180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDownIcon className='h-6 w-6 text-white' />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='overflow-hidden'
          >
            <div className='flex flex-col items-center px-6 border-b border-white/20'>
              {item.bestValue && (
                <div className='pt-6'>
                  <BestValueBadge
                    classes={{
                      parantClass:
                        'bg-gradient-to-b from-[#CD4FF2] to-[#5F4AFF]',
                      textParant: 'bg-[#170F33]',
                    }}
                  />
                </div>
              )}
              <Pricing
                className='pt-6'
                price={item.price}
                priceCut={item.priceCut}
              />
              <Tooltip title={item.buttonTooltip}>
                <ButtonDefault
                  disabled={item.disabled}
                  onClick={item.onClick}
                  className='w-full px-6 py-[9px] text-sm font-semibold leading-[22px] mb-6 disabled:bg-grayZl-900 disabled:text-grayZl-700'
                  size={0}
                  variant={ButtonVariants.PRIMARY}
                >
                  {item.buttonText}
                </ButtonDefault>
              </Tooltip>
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>What is inside</p>
              <p className={`${titleClass} flex-1 text-end`}>
                {item.whatInside}
              </p>
            </div>

            <div className='px-6 py-[11px] bg-grayZl-950'>
              <p className='text-[10px] leading-[18px] text-primary tracking-[1.5px] uppercase font-semibold'>
                What you will get:
              </p>
            </div>

            <div className='border-b border-white/20'>
              <div className='ms-7 border-l border-white/20'>
                <div className='flex items-center ps-3 pe-6 py-[13px]'>
                  <p className={`${titleClass} flex-1`}>Digital Copy</p>
                  <p className={`${titleClass} flex-1 text-end`}>
                    {item.digital}
                  </p>
                </div>
                <div className='flex items-center ps-3 pe-6 py-[13px]'>
                  <p className={`${titleClass} flex-1`}>Hard Copy</p>
                  <p className={`${titleClass} flex-1 text-end`}>{item.hard}</p>
                </div>
              </div>
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>Duration</p>
              <p className={`${titleClass} flex-1 text-end`}>{item.duration}</p>
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>Validity</p>
              <p className={`${titleClass} flex-1 text-end`}>{item.validity}</p>
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>Free Shipping</p>
              {item.freeShipping && (
                <CheckIcon className='w-[22px] h-[22px] text-success-400' />
              )}
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>App Access</p>
              {item.appAccess && (
                <CheckIcon className='w-[22px] h-[22px] text-success-400' />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Item;
